/**
 * Four languages, two of them right-to-left.
 *
 * In shared housing the resident is often not an English speaker, and this page
 * is the only interface they have to their own tenancy. Getting this wrong does
 * not degrade the experience — it removes it.
 *
 * Two decisions worth knowing:
 *
 * 1. The picker sits on the sign-in screen, *before* authentication. Somebody
 *    who cannot read that page cannot reach a settings menu to change it.
 *
 * 2. Direction is applied once, at the document level, when the language is
 *    chosen. Flipping a live layout is where RTL bugs come from; reloading into
 *    the right direction is unglamorous and correct.
 */

export type Lang = 'en' | 'ar' | 'ur' | 'hi';

export interface LangMeta {
  code: Lang;
  /** In its own script, because that is what a speaker recognises. */
  label: string;
  english: string;
  dir: 'ltr' | 'rtl';
  font: string;
}

export const LANGUAGES: LangMeta[] = [
  { code: 'en', label: 'English', english: 'English', dir: 'ltr', font: "'IBM Plex Sans', sans-serif" },
  { code: 'ar', label: 'العربية', english: 'Arabic', dir: 'rtl', font: "'Noto Sans Arabic', sans-serif" },
  { code: 'ur', label: 'اردو', english: 'Urdu', dir: 'rtl', font: "'Noto Sans Arabic', sans-serif" },
  { code: 'hi', label: 'हिन्दी', english: 'Hindi', dir: 'ltr', font: "'Noto Sans Devanagari', sans-serif" },
];

export const meta = (lang: Lang): LangMeta =>
  LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0]!;

/**
 * English is the reference. `scripts/check-i18n.mjs` fails the build if any
 * other language is missing a key that exists here, because the fallback —
 * showing English to somebody who chose Urdu — is silent and looks fine to
 * whoever wrote the code.
 */
const en = {
  'app.name': 'BurjManage',
  'app.tagline': 'Your tenancy',

  'signin.title': 'Sign in',
  'signin.intro': 'Enter the mobile number your tenancy is registered against. We will text you a code.',
  'signin.phone': 'Mobile number',
  'signin.send': 'Send me a code',
  'signin.sending': 'Sending…',
  'signin.code': 'Six-digit code',
  'signin.codeSent': 'We sent a code to {phone}. It expires in ten minutes.',
  'signin.verify': 'Sign in',
  'signin.verifying': 'Checking…',
  'signin.resend': 'Send another code',
  'signin.change': 'Use a different number',
  'signin.noPassword': 'There is no password to remember. We text you a code each time.',
  'signin.trouble': 'Not receiving codes? Call {phone}.',
  'signin.language': 'Language',

  'nav.home': 'Home',
  'nav.payments': 'Payments',
  'nav.report': 'Report',
  'nav.signout': 'Sign out',

  'home.greeting': 'Hello, {name}',
  'home.yourRoom': 'Your room',
  'home.rentDue': 'Rent due',
  'home.dueOn': 'Due {date}',
  'home.overdue': '{days} days late',
  'home.dueToday': 'Due today',
  'home.noRentDue': 'No rent is due right now.',
  'home.rentPaused': 'Your rent is paused while a repair is dealt with. You do not need to pay for now.',
  'home.monthlyRent': 'Monthly rent',
  'home.deposit': 'Deposit held',
  'home.tenancy': 'Tenancy',
  'home.until': 'Until {date}',
  'home.endingSoon': 'Your tenancy ends in {days} days. Someone will be in touch about renewing.',
  'home.howToPay': 'How to pay',
  'home.howToPayBody': 'Pay the office by transfer or in person. Once it is recorded you will see it under Payments, and you can show that screen as proof.',
  'pay.cta': 'I\'ve paid — tell the office',
  'pay.title': 'Tell us you have paid',
  'pay.intro': 'Nothing is charged here. The office checks your payment against the bank, then it appears under Payments.',
  'pay.how': 'How did you pay?',
  'pay.BankTransfer': 'Bank transfer',
  'pay.Cheque': 'Cheque',
  'pay.CreditCard': 'Card, at the office',
  'pay.Cash': 'Cash — please collect it',
  'pay.reference': 'Reference (optional)',
  'pay.referenceHint': 'The transfer or cheque number helps the office find it faster.',
  'pay.send': 'Send',
  'pay.sent': 'Thank you. The office will confirm it, usually within one working day.',
  'pay.cashSent': 'The office has been told. Someone will arrange to collect the cash.',
  'home.reported': 'You told us this is paid. The office is checking it.',
  'home.contact': 'Your contact',
  'home.callOffice': 'Call the office',
  'home.shared': 'Using a shared computer? Sign out when you are done.',

  'payments.title': 'Payments',
  'payments.none': 'No payments recorded yet.',
  'payments.received': 'Received {date}',
  'payments.method': 'Paid by {method}',
  'payments.proof': 'This is your record. It is written by the office when a payment is confirmed, and it cannot be edited afterwards.',
  'payments.kind.rent': 'Rent',
  'payments.kind.deposit': 'Deposit',
  'payments.kind.other': 'Other',

  'report.title': 'Report a problem',
  'report.intro': 'Tell us what is wrong. Someone will be assigned to it.',
  'report.category': 'What is the problem?',
  'report.describe': 'Describe it',
  'report.placeholder': 'Water coming through the bathroom ceiling since this morning',
  'report.send': 'Send',
  'report.sending': 'Sending…',
  'report.sent': 'Reported. Someone will be assigned and will contact you.',
  'report.emergency': 'This is an emergency',
  'report.emergencyHelp': 'Choose this only if the room is unsafe or unusable right now — flooding, no power, a broken lock, fire risk. It goes to someone immediately.',
  'report.emergencyOn': 'This will be treated as urgent and someone will be sent today.',
  'report.callInstead': 'If someone is in danger, call the emergency services first, then tell us.',

  'category.Plumbing': 'Water or plumbing',
  'category.Electrical': 'Electricity',
  'category.AirConditioning': 'Air conditioning',
  'category.Appliance': 'An appliance',
  'category.Internet': 'Internet',
  'category.Lock': 'Door or lock',
  'category.Cleaning': 'Cleanliness',
  'category.Pest': 'Pests',
  'category.Other': 'Something else',

  'error.network': 'We cannot reach the system. Check your connection and try again.',
  'error.badCode': 'That code is not right, or it has expired. Ask for another.',
  'error.noTenancy': 'We cannot find an active tenancy for that number. Call the office.',
  'error.badPhone': 'That does not look like a phone number. Use the number the office has, e.g. 050 123 4567.',
  'error.throttled': 'Too many tries. Wait 15 minutes, then ask for a new code.',
  'error.generic': 'Something went wrong. Please try again.',
  'error.retry': 'Try again',

  'common.loading': 'Loading…',
  'common.close': 'Close',
  'common.back': 'Back',
} as const;

export type Key = keyof typeof en;

const ar: Record<Key, string> = {
  'app.name': 'برج مانج',
  'app.tagline': 'إيجارك',
  'signin.title': 'تسجيل الدخول',
  'signin.intro': 'أدخل رقم الهاتف المسجل في عقد الإيجار. سنرسل لك رمزًا برسالة نصية.',
  'signin.phone': 'رقم الهاتف',
  'signin.send': 'أرسل لي رمزًا',
  'signin.sending': 'جارٍ الإرسال…',
  'signin.code': 'الرمز المكوّن من ستة أرقام',
  'signin.codeSent': 'أرسلنا رمزًا إلى {phone}. تنتهي صلاحيته خلال عشر دقائق.',
  'signin.verify': 'دخول',
  'signin.verifying': 'جارٍ التحقق…',
  'signin.resend': 'أرسل رمزًا آخر',
  'signin.change': 'استخدم رقمًا آخر',
  'signin.noPassword': 'لا توجد كلمة مرور تحتاج إلى تذكّرها. نرسل لك رمزًا في كل مرة.',
  'signin.trouble': 'لا تصلك الرموز؟ اتصل على {phone}.',
  'signin.language': 'اللغة',
  'nav.home': 'الرئيسية',
  'nav.payments': 'المدفوعات',
  'nav.report': 'إبلاغ',
  'nav.signout': 'تسجيل الخروج',
  'home.greeting': 'مرحبًا، {name}',
  'home.yourRoom': 'غرفتك',
  'home.rentDue': 'الإيجار المستحق',
  'home.dueOn': 'مستحق في {date}',
  'home.overdue': 'متأخر {days} يومًا',
  'home.dueToday': 'مستحق اليوم',
  'home.noRentDue': 'لا يوجد إيجار مستحق حاليًا.',
  'home.rentPaused': 'تم إيقاف إيجارك مؤقتًا أثناء معالجة الإصلاح. لا حاجة للدفع في الوقت الحالي.',
  'home.monthlyRent': 'الإيجار الشهري',
  'home.deposit': 'التأمين المحتفظ به',
  'home.tenancy': 'مدة الإيجار',
  'home.until': 'حتى {date}',
  'home.endingSoon': 'ينتهي عقد إيجارك خلال {days} يومًا. سيتواصل معك أحد بشأن التجديد.',
  'home.howToPay': 'كيفية الدفع',
  'home.howToPayBody': 'ادفع للمكتب عن طريق التحويل أو شخصيًا. بمجرد تسجيل الدفعة ستظهر ضمن المدفوعات، ويمكنك إظهار تلك الشاشة كإثبات.',
  'pay.cta': 'دفعتُ — أبلغ المكتب',
  'pay.title': 'أخبرنا بأنك دفعت',
  'pay.intro': 'لن يُخصم أي مبلغ هنا. يتحقق المكتب من دفعتك مع البنك، ثم تظهر ضمن المدفوعات.',
  'pay.how': 'كيف دفعت؟',
  'pay.BankTransfer': 'تحويل بنكي',
  'pay.Cheque': 'شيك',
  'pay.CreditCard': 'بطاقة، في المكتب',
  'pay.Cash': 'نقدًا — يرجى التحصيل',
  'pay.reference': 'رقم المرجع (اختياري)',
  'pay.referenceHint': 'رقم التحويل أو الشيك يساعد المكتب على إيجاد الدفعة أسرع.',
  'pay.send': 'إرسال',
  'pay.sent': 'شكرًا لك. سيؤكد المكتب الدفعة، عادةً خلال يوم عمل واحد.',
  'pay.cashSent': 'تم إبلاغ المكتب. سيرتب أحدهم استلام المبلغ نقدًا.',
  'home.reported': 'أخبرتنا أن هذا المبلغ مدفوع. المكتب يتحقق منه.',
  'home.contact': 'جهة الاتصال',
  'home.callOffice': 'اتصل بالمكتب',
  'home.shared': 'تستخدم جهازاً مشتركاً؟ سجّل الخروج عند الانتهاء.',
  'payments.title': 'المدفوعات',
  'payments.none': 'لا توجد مدفوعات مسجلة بعد.',
  'payments.received': 'استُلمت في {date}',
  'payments.method': 'دُفعت بواسطة {method}',
  'payments.proof': 'هذا سجلك. يكتبه المكتب عند تأكيد الدفعة، ولا يمكن تعديله بعد ذلك.',
  'payments.kind.rent': 'إيجار',
  'payments.kind.deposit': 'تأمين',
  'payments.kind.other': 'أخرى',
  'report.title': 'الإبلاغ عن مشكلة',
  'report.intro': 'أخبرنا بما هو معطل. سيتم تكليف شخص بذلك.',
  'report.category': 'ما هي المشكلة؟',
  'report.describe': 'اشرح المشكلة',
  'report.placeholder': 'تسرب مياه من سقف الحمام منذ هذا الصباح',
  'report.send': 'إرسال',
  'report.sending': 'جارٍ الإرسال…',
  'report.sent': 'تم الإبلاغ. سيتم تكليف شخص وسيتواصل معك.',
  'report.emergency': 'هذه حالة طارئة',
  'report.emergencyHelp': 'اختر هذا فقط إذا كانت الغرفة غير آمنة أو غير صالحة للاستخدام الآن — فيضان، انقطاع كهرباء، قفل مكسور، خطر حريق. سيصل إلى شخص فورًا.',
  'report.emergencyOn': 'سيُعامل هذا كأمر عاجل وسيتم إرسال شخص اليوم.',
  'report.callInstead': 'إذا كان هناك خطر على شخص ما، اتصل بالطوارئ أولًا ثم أخبرنا.',
  'category.Plumbing': 'المياه أو السباكة',
  'category.Electrical': 'الكهرباء',
  'category.AirConditioning': 'التكييف',
  'category.Appliance': 'جهاز منزلي',
  'category.Internet': 'الإنترنت',
  'category.Lock': 'الباب أو القفل',
  'category.Cleaning': 'النظافة',
  'category.Pest': 'الحشرات',
  'category.Other': 'شيء آخر',
  'error.network': 'لا يمكننا الوصول إلى النظام. تحقق من اتصالك وحاول مرة أخرى.',
  'error.badCode': 'الرمز غير صحيح أو انتهت صلاحيته. اطلب رمزًا آخر.',
  'error.noTenancy': 'لم نجد عقد إيجار نشطًا لهذا الرقم. اتصل بالمكتب.',
  'error.badPhone': 'هذا لا يبدو رقم هاتف. استخدم الرقم المسجل لدى المكتب، مثل 050 123 4567.',
  'error.throttled': 'محاولات كثيرة جدًا. انتظر 15 دقيقة ثم اطلب رمزًا جديدًا.',
  'error.generic': 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
  'error.retry': 'حاول مرة أخرى',
  'common.loading': 'جارٍ التحميل…',
  'common.close': 'إغلاق',
  'common.back': 'رجوع',
};

const ur: Record<Key, string> = {
  'app.name': 'برج مینج',
  'app.tagline': 'آپ کی رہائش',
  'signin.title': 'سائن ان',
  'signin.intro': 'وہ موبائل نمبر درج کریں جس پر آپ کا کرایہ نامہ رجسٹرڈ ہے۔ ہم آپ کو کوڈ بھیجیں گے۔',
  'signin.phone': 'موبائل نمبر',
  'signin.send': 'مجھے کوڈ بھیجیں',
  'signin.sending': 'بھیجا جا رہا ہے…',
  'signin.code': 'چھ ہندسوں کا کوڈ',
  'signin.codeSent': 'ہم نے {phone} پر کوڈ بھیج دیا ہے۔ یہ دس منٹ میں ختم ہو جائے گا۔',
  'signin.verify': 'داخل ہوں',
  'signin.verifying': 'جانچا جا رہا ہے…',
  'signin.resend': 'دوبارہ کوڈ بھیجیں',
  'signin.change': 'دوسرا نمبر استعمال کریں',
  'signin.noPassword': 'کوئی پاس ورڈ یاد رکھنے کی ضرورت نہیں۔ ہم ہر بار کوڈ بھیجتے ہیں۔',
  'signin.trouble': 'کوڈ نہیں مل رہا؟ {phone} پر کال کریں۔',
  'signin.language': 'زبان',
  'nav.home': 'ہوم',
  'nav.payments': 'ادائیگیاں',
  'nav.report': 'اطلاع دیں',
  'nav.signout': 'سائن آؤٹ',
  'home.greeting': 'السلام علیکم، {name}',
  'home.yourRoom': 'آپ کا کمرہ',
  'home.rentDue': 'واجب الادا کرایہ',
  'home.dueOn': '{date} کو واجب',
  'home.overdue': '{days} دن تاخیر',
  'home.dueToday': 'آج واجب',
  'home.noRentDue': 'اس وقت کوئی کرایہ واجب نہیں۔',
  'home.rentPaused': 'مرمت کے دوران آپ کا کرایہ روک دیا گیا ہے۔ فی الحال ادائیگی کی ضرورت نہیں۔',
  'home.monthlyRent': 'ماہانہ کرایہ',
  'home.deposit': 'رکھی گئی ضمانت',
  'home.tenancy': 'مدت',
  'home.until': '{date} تک',
  'home.endingSoon': 'آپ کی مدت {days} دن میں ختم ہو رہی ہے۔ تجدید کے بارے میں رابطہ کیا جائے گا۔',
  'home.howToPay': 'ادائیگی کیسے کریں',
  'home.howToPayBody': 'دفتر کو ٹرانسفر یا بالمشافہ ادائیگی کریں۔ درج ہوتے ہی وہ ادائیگیوں میں نظر آئے گی اور آپ اسے ثبوت کے طور پر دکھا سکتے ہیں۔',
  'pay.cta': 'میں نے ادائیگی کر دی — دفتر کو بتائیں',
  'pay.title': 'ہمیں بتائیں کہ آپ نے ادائیگی کر دی ہے',
  'pay.intro': 'یہاں کوئی رقم نہیں کاٹی جاتی۔ دفتر بینک سے آپ کی ادائیگی کی تصدیق کرتا ہے، پھر یہ ادائیگیوں میں نظر آتی ہے۔',
  'pay.how': 'آپ نے کیسے ادائیگی کی؟',
  'pay.BankTransfer': 'بینک ٹرانسفر',
  'pay.Cheque': 'چیک',
  'pay.CreditCard': 'کارڈ، دفتر میں',
  'pay.Cash': 'نقد — براہِ کرم وصول کریں',
  'pay.reference': 'حوالہ نمبر (اختیاری)',
  'pay.referenceHint': 'ٹرانسفر یا چیک نمبر سے دفتر اسے جلدی ڈھونڈ لیتا ہے۔',
  'pay.send': 'بھیجیں',
  'pay.sent': 'شکریہ۔ دفتر اس کی تصدیق کرے گا، عام طور پر ایک کاروباری دن میں۔',
  'pay.cashSent': 'دفتر کو بتا دیا گیا ہے۔ کوئی نقد رقم وصول کرنے کا انتظام کرے گا۔',
  'home.reported': 'آپ نے بتایا ہے کہ یہ ادا ہو چکا ہے۔ دفتر اس کی جانچ کر رہا ہے۔',
  'home.contact': 'آپ کا رابطہ',
  'home.callOffice': 'دفتر کو کال کریں',
  'home.shared': 'مشترکہ کمپیوٹر؟ کام مکمل ہونے پر سائن آؤٹ کریں۔',
  'payments.title': 'ادائیگیاں',
  'payments.none': 'ابھی تک کوئی ادائیگی درج نہیں۔',
  'payments.received': '{date} کو موصول',
  'payments.method': '{method} کے ذریعے ادا',
  'payments.proof': 'یہ آپ کا ریکارڈ ہے۔ ادائیگی کی تصدیق پر دفتر اسے درج کرتا ہے اور بعد میں تبدیل نہیں ہو سکتا۔',
  'payments.kind.rent': 'کرایہ',
  'payments.kind.deposit': 'ضمانت',
  'payments.kind.other': 'دیگر',
  'report.title': 'مسئلے کی اطلاع دیں',
  'report.intro': 'ہمیں بتائیں کیا خرابی ہے۔ کسی کو ذمہ داری دی جائے گی۔',
  'report.category': 'مسئلہ کیا ہے؟',
  'report.describe': 'تفصیل بتائیں',
  'report.placeholder': 'آج صبح سے باتھ روم کی چھت سے پانی آ رہا ہے',
  'report.send': 'بھیجیں',
  'report.sending': 'بھیجا جا رہا ہے…',
  'report.sent': 'اطلاع درج ہو گئی۔ کوئی مقرر ہو کر آپ سے رابطہ کرے گا۔',
  'report.emergency': 'یہ ہنگامی صورتحال ہے',
  'report.emergencyHelp': 'یہ صرف تب منتخب کریں جب کمرہ ابھی غیر محفوظ یا ناقابلِ استعمال ہو — سیلاب، بجلی بند، ٹوٹا تالا، آگ کا خطرہ۔ یہ فوراً کسی تک پہنچے گا۔',
  'report.emergencyOn': 'اسے فوری سمجھا جائے گا اور آج ہی کسی کو بھیجا جائے گا۔',
  'report.callInstead': 'اگر کسی کی جان کو خطرہ ہے تو پہلے ایمرجنسی سروس کو کال کریں، پھر ہمیں بتائیں۔',
  'category.Plumbing': 'پانی یا پلمبنگ',
  'category.Electrical': 'بجلی',
  'category.AirConditioning': 'ایئر کنڈیشننگ',
  'category.Appliance': 'کوئی آلہ',
  'category.Internet': 'انٹرنیٹ',
  'category.Lock': 'دروازہ یا تالا',
  'category.Cleaning': 'صفائی',
  'category.Pest': 'کیڑے مکوڑے',
  'category.Other': 'کچھ اور',
  'error.network': 'ہم سسٹم تک نہیں پہنچ سکے۔ اپنا کنکشن دیکھ کر دوبارہ کوشش کریں۔',
  'error.badCode': 'یہ کوڈ درست نہیں یا ختم ہو چکا ہے۔ نیا کوڈ منگوائیں۔',
  'error.noTenancy': 'اس نمبر پر کوئی فعال کرایہ نامہ نہیں ملا۔ دفتر سے رابطہ کریں۔',
  'error.badPhone': 'یہ فون نمبر نہیں لگتا۔ وہی نمبر لکھیں جو دفتر کے پاس ہے، جیسے 050 123 4567۔',
  'error.throttled': 'بہت زیادہ کوششیں۔ 15 منٹ انتظار کریں، پھر نیا کوڈ منگوائیں۔',
  'error.generic': 'کچھ غلط ہو گیا۔ دوبارہ کوشش کریں۔',
  'error.retry': 'دوبارہ کوشش کریں',
  'common.loading': 'لوڈ ہو رہا ہے…',
  'common.close': 'بند کریں',
  'common.back': 'واپس',
};

const hi: Record<Key, string> = {
  'app.name': 'बurjमैनेज',
  'app.tagline': 'आपकी किरायेदारी',
  'signin.title': 'साइन इन',
  'signin.intro': 'वह मोबाइल नंबर डालें जिस पर आपकी किरायेदारी दर्ज है। हम आपको कोड भेजेंगे।',
  'signin.phone': 'मोबाइल नंबर',
  'signin.send': 'मुझे कोड भेजें',
  'signin.sending': 'भेजा जा रहा है…',
  'signin.code': 'छह अंकों का कोड',
  'signin.codeSent': 'हमने {phone} पर कोड भेजा है। यह दस मिनट में समाप्त हो जाएगा।',
  'signin.verify': 'प्रवेश करें',
  'signin.verifying': 'जाँच हो रही है…',
  'signin.resend': 'दूसरा कोड भेजें',
  'signin.change': 'दूसरा नंबर इस्तेमाल करें',
  'signin.noPassword': 'याद रखने के लिए कोई पासवर्ड नहीं है। हम हर बार कोड भेजते हैं।',
  'signin.trouble': 'कोड नहीं मिल रहा? {phone} पर कॉल करें।',
  'signin.language': 'भाषा',
  'nav.home': 'होम',
  'nav.payments': 'भुगतान',
  'nav.report': 'शिकायत',
  'nav.signout': 'साइन आउट',
  'home.greeting': 'नमस्ते, {name}',
  'home.yourRoom': 'आपका कमरा',
  'home.rentDue': 'देय किराया',
  'home.dueOn': '{date} को देय',
  'home.overdue': '{days} दिन की देरी',
  'home.dueToday': 'आज देय',
  'home.noRentDue': 'अभी कोई किराया देय नहीं है।',
  'home.rentPaused': 'मरम्मत होने तक आपका किराया रोका गया है। फिलहाल भुगतान की ज़रूरत नहीं।',
  'home.monthlyRent': 'मासिक किराया',
  'home.deposit': 'जमा राशि',
  'home.tenancy': 'अवधि',
  'home.until': '{date} तक',
  'home.endingSoon': 'आपकी अवधि {days} दिन में समाप्त हो रही है। नवीनीकरण के बारे में कोई संपर्क करेगा।',
  'home.howToPay': 'भुगतान कैसे करें',
  'home.howToPayBody': 'कार्यालय को ट्रांसफ़र से या व्यक्तिगत रूप से भुगतान करें। दर्ज होते ही वह भुगतान में दिखेगा और आप उसे प्रमाण के रूप में दिखा सकते हैं।',
  'pay.cta': 'मैंने भुगतान कर दिया — ऑफ़िस को बताएँ',
  'pay.title': 'हमें बताएँ कि आपने भुगतान कर दिया है',
  'pay.intro': 'यहाँ कोई पैसा नहीं कटता। ऑफ़िस बैंक से आपके भुगतान की जाँच करता है, फिर यह भुगतान में दिखता है।',
  'pay.how': 'आपने कैसे भुगतान किया?',
  'pay.BankTransfer': 'बैंक ट्रांसफ़र',
  'pay.Cheque': 'चेक',
  'pay.CreditCard': 'कार्ड, ऑफ़िस में',
  'pay.Cash': 'नकद — कृपया आकर ले जाएँ',
  'pay.reference': 'संदर्भ संख्या (वैकल्पिक)',
  'pay.referenceHint': 'ट्रांसफ़र या चेक नंबर से ऑफ़िस इसे जल्दी ढूँढ लेता है।',
  'pay.send': 'भेजें',
  'pay.sent': 'धन्यवाद। ऑफ़िस इसकी पुष्टि करेगा, आमतौर पर एक कार्यदिवस में।',
  'pay.cashSent': 'ऑफ़िस को बता दिया गया है। कोई नकद लेने की व्यवस्था करेगा।',
  'home.reported': 'आपने बताया है कि यह भुगतान हो चुका है। ऑफ़िस इसकी जाँच कर रहा है।',
  'home.contact': 'आपका संपर्क',
  'home.callOffice': 'ऑफ़िस को कॉल करें',
  'home.shared': 'साझा कंप्यूटर? काम पूरा होने पर साइन आउट करें।',
  'payments.title': 'भुगतान',
  'payments.none': 'अभी तक कोई भुगतान दर्ज नहीं।',
  'payments.received': '{date} को प्राप्त',
  'payments.method': '{method} से भुगतान',
  'payments.proof': 'यह आपका रिकॉर्ड है। भुगतान की पुष्टि पर कार्यालय इसे दर्ज करता है और बाद में बदला नहीं जा सकता।',
  'payments.kind.rent': 'किराया',
  'payments.kind.deposit': 'जमा',
  'payments.kind.other': 'अन्य',
  'report.title': 'समस्या की सूचना दें',
  'report.intro': 'बताइए क्या खराब है। किसी को ज़िम्मेदारी दी जाएगी।',
  'report.category': 'समस्या क्या है?',
  'report.describe': 'विवरण दें',
  'report.placeholder': 'आज सुबह से बाथरूम की छत से पानी आ रहा है',
  'report.send': 'भेजें',
  'report.sending': 'भेजा जा रहा है…',
  'report.sent': 'सूचना दर्ज हुई। किसी को नियुक्त किया जाएगा और वे संपर्क करेंगे।',
  'report.emergency': 'यह आपात स्थिति है',
  'report.emergencyHelp': 'यह तभी चुनें जब कमरा अभी असुरक्षित या अनुपयोगी हो — पानी भरना, बिजली न होना, टूटा ताला, आग का ख़तरा। यह तुरंत किसी तक पहुँचेगा।',
  'report.emergencyOn': 'इसे अत्यावश्यक माना जाएगा और आज ही किसी को भेजा जाएगा।',
  'report.callInstead': 'यदि किसी की जान को ख़तरा है तो पहले आपातकालीन सेवा को कॉल करें, फिर हमें बताएँ।',
  'category.Plumbing': 'पानी या नलसाज़ी',
  'category.Electrical': 'बिजली',
  'category.AirConditioning': 'एयर कंडीशनिंग',
  'category.Appliance': 'कोई उपकरण',
  'category.Internet': 'इंटरनेट',
  'category.Lock': 'दरवाज़ा या ताला',
  'category.Cleaning': 'सफ़ाई',
  'category.Pest': 'कीड़े',
  'category.Other': 'कुछ और',
  'error.network': 'हम सिस्टम तक नहीं पहुँच सके। कनेक्शन जाँचकर दोबारा कोशिश करें।',
  'error.badCode': 'यह कोड सही नहीं है या समाप्त हो गया है। दूसरा माँगें।',
  'error.noTenancy': 'इस नंबर पर कोई सक्रिय किरायेदारी नहीं मिली। कार्यालय को कॉल करें।',
  'error.badPhone': 'यह फ़ोन नंबर नहीं लगता। वही नंबर लिखें जो कार्यालय के पास है, जैसे 050 123 4567।',
  'error.throttled': 'बहुत ज़्यादा कोशिशें। 15 मिनट रुकें, फिर नया कोड माँगें।',
  'error.generic': 'कुछ गड़बड़ हुई। कृपया दोबारा कोशिश करें।',
  'error.retry': 'दोबारा कोशिश करें',
  'common.loading': 'लोड हो रहा है…',
  'common.close': 'बंद करें',
  'common.back': 'वापस',
};

export const DICTIONARIES: Record<Lang, Record<Key, string>> = { en, ar, ur, hi };

const STORAGE_KEY = 'burj.tenant.lang';

/**
 * Best guess from the browser, then whatever they last chose.
 *
 * A resident arriving from an SMS link should land in a language they can read
 * without touching anything, and never be moved out of one they picked.
 */
export function detectLang(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && LANGUAGES.some((l) => l.code === saved)) return saved as Lang;
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.toLowerCase().split('-')[0];
    if (LANGUAGES.some((l) => l.code === base)) return base as Lang;
  }
  return 'en';
}

export function applyLang(lang: Lang): void {
  const m = meta(lang);
  localStorage.setItem(STORAGE_KEY, lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = m.dir;
  document.documentElement.style.setProperty('--font', m.font);
}

/** `t('home.greeting', { name: 'Aisha' })` */
export function translator(lang: Lang) {
  const dict = DICTIONARIES[lang];
  return (key: Key, vars?: Record<string, string | number>): string => {
    // English is the fallback of last resort. It should never fire —
    // scripts/check-i18n.mjs fails the build if a key is missing anywhere.
    let out: string = dict[key] ?? en[key] ?? key;
    // split/join rather than replaceAll: replaceAll is ES2021 and this app is
    // built for ES2018 because residents open it on old, cheap phones. A method
    // a bundler cannot transpile would throw on exactly the devices that matter.
    if (vars) for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
    return out;
  };
}

export type T = ReturnType<typeof translator>;
